import PropTypes from 'prop-types';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { haptic } from '../utils/telegram';

const CartContext = createContext(null);

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart должен использоваться внутри CartProvider');
  return ctx;
};

const STORAGE_KEY = 'ck_cart';

CartProvider.propTypes = { children: PropTypes.node };

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const add = (dish, quantity = 1) => {
    haptic('light');
    setItems((prev) => {
      const existing = prev.find((i) => i.dishId === dish.id);
      if (existing) {
        return prev.map((i) => (i.dishId === dish.id ? { ...i, quantity: i.quantity + quantity } : i));
      }
      return [
        ...prev,
        {
          dishId: dish.id,
          quantity,
          name: dish.name,
          emoji: dish.emoji,
          imageUrl: dish.imageUrl,
          price: dish.price,
          finalPrice: dish.finalPrice ?? dish.price,
          discountPercent: dish.discountPercent ?? 0,
          calories: dish.calories,
          categoryColor: dish.category?.color || dish.categoryColor,
        },
      ];
    });
  };

  const setQuantity = (dishId, quantity) => {
    haptic('light');
    setItems((prev) =>
      quantity <= 0
        ? prev.filter((i) => i.dishId !== dishId)
        : prev.map((i) => (i.dishId === dishId ? { ...i, quantity } : i))
    );
  };

  const remove = (dishId) => setQuantity(dishId, 0);

  const clear = () => setItems([]);

  const quantityOf = (dishId) => items.find((i) => i.dishId === dishId)?.quantity || 0;

  const count = items.reduce((s, i) => s + i.quantity, 0);
  const subtotal = items.reduce((s, i) => s + (i.finalPrice ?? i.price) * i.quantity, 0);
  const calories = items.reduce((s, i) => s + (i.calories || 0) * i.quantity, 0);

  const value = useMemo(
    () => ({ items, add, setQuantity, remove, clear, quantityOf, count, subtotal, calories }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}
